import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Input, Option, Select, Sheet,
    Stack, Table, Typography,
} from '@mui/joy';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { useAdmin } from '../../../AdminContext';
import { APIAdminAdsCampaigns, adminAdsError } from '../../../api_admin_ads';
import AdStatusChip from '../../../components/Ads/AdStatusChip';
import { PRICING_LABELS, formatMoney } from '../../../components/Ads/adsConstants';


const STATUS_OPTIONS = [
    { value: '', label: 'Все статусы' },
    { value: 'moderation', label: 'На модерации' },
    { value: 'active', label: 'Идут показы' },
    { value: 'scheduled', label: 'Запланированы' },
    { value: 'paused', label: 'Остановлены' },
    { value: 'out_of_budget', label: 'Закончился бюджет' },
    { value: 'rejected', label: 'Отклонены' },
    { value: 'draft', label: 'Черновики' },
    { value: 'completed,archived', label: 'Завершены и архив' },
];


export default function AdminAdsCampaignsPage() {
    const navigate = useNavigate();
    const { adminToken: token } = useAdmin();

    const [items, setItems] = useState([]);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => { setPage(1); setSearch(searchInput); }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdminAdsCampaigns(token, {
                status: status || undefined,
                search: search || undefined,
                page, page_size: 30,
            });
            setItems(res?.items || []);
            setPages(res?.pages || 1);
            setTotal(res?.total || 0);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить кампании'));
        } finally {
            setLoading(false);
        }
    }, [token, status, search, page]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Typography level="h4" sx={{ mb: 0.5 }}>Все рекламные кампании</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Найдено: {total}
            </Typography>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Input
                        size="sm"
                        placeholder="Название кампании или рекламодатель"
                        startDecorator={<SearchRoundedIcon />}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ flex: 1, minWidth: 240 }}
                    />
                    <Select
                        size="sm"
                        value={status}
                        onChange={(_, v) => { setStatus(v ?? ''); setPage(1); }}
                        sx={{ minWidth: 200 }}
                    >
                        {STATUS_OPTIONS.map((o) => (
                            <Option key={o.value} value={o.value}>{o.label}</Option>
                        ))}
                    </Select>
                </Stack>
            </Sheet>

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow stickyHeader sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>ID</th>
                                <th>Кампания</th>
                                <th style={{ width: 200 }}>Рекламодатель</th>
                                <th style={{ width: 150 }}>Статус</th>
                                <th style={{ width: 110 }}>Оплата</th>
                                <th style={{ width: 130 }}>Бюджет/сут</th>
                                <th style={{ width: 130 }}>Потрачено</th>
                                <th style={{ width: 120 }}>Создана</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((campaign) => (
                                <tr
                                    key={campaign.id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/admin/ads/campaigns/${campaign.id}`)}
                                >
                                    <td>{campaign.id}</td>
                                    <td>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                            {campaign.name}
                                        </Typography>
                                        {campaign.creatives_pending > 0 && (
                                            <Typography level="body-xs" color="warning">
                                                на проверке: {campaign.creatives_pending}
                                            </Typography>
                                        )}
                                    </td>
                                    <td>
                                        <Typography level="body-sm">{campaign.advertiser}</Typography>
                                        <Typography level="body-xs" color="neutral">
                                            аккаунт #{campaign.account_id}
                                        </Typography>
                                    </td>
                                    <td>
                                        <AdStatusChip
                                            status={campaign.status}
                                            systemPaused={campaign.system_paused}
                                            systemPauseReason={campaign.system_pause_reason}
                                        />
                                    </td>
                                    <td>{PRICING_LABELS[campaign.pricing_model]}</td>
                                    <td>{formatMoney(campaign.daily_budget)} ₽</td>
                                    <td>{formatMoney(campaign.spent_total)} ₽</td>
                                    <td>
                                        {campaign.date_create
                                            ? format(new Date(campaign.date_create), 'd MMM yy', { locale: ru })
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={8}>
                                        <Typography level="body-sm" color="neutral" sx={{ py: 3, textAlign: 'center' }}>
                                            Ничего не найдено
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        Назад
                    </Button>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>{page} / {pages}</Typography>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                        Вперёд
                    </Button>
                </Stack>
            )}
        </Box>
    );
}